import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaFileInvoiceDollar, FaCheck, FaTimes, FaDownload, FaMoneyBillWave, FaEye } from 'react-icons/fa';
import DataTable from '../shared/DataTable';
import Loader from '../../shared/Loader';
import InvoiceModal from './InvoiceModal';
import { showSuccess, showConfirm, showLoading, closeLoading, showError } from '../../../utils/Alert';
import './Payments.css';

const Payments = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        fetchTransactions();
    }, []);

    const fetchTransactions = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/admin/invoices', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTransactions(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching transactions", err);
            showError("System Error", "Could not synchronize platform transactions.");
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (transactions.length === 0) {
            showError("Export Error", "No transaction records found to export.");
            return;
        }

        showLoading('Generating Master Ledger...');
        try {
            const headers = ["Invoice No", "Course", "Student Name", "Student Email", "Amount ($)", "Date", "Status"];
            const csvContent = [
                headers.join(","),
                ...transactions.map(tx => [
                    tx.invoiceNo,
                    `"${tx.courseId?.title}"`,
                    tx.studentId?.name,
                    tx.studentId?.email,
                    tx.amount,
                    new Date(tx.date).toLocaleDateString(),
                    tx.status
                ].join(","))
            ].join("\n");

            const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Master_Ledger_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 150);

            showSuccess("Export Complete", "Master ledger successfully downloaded.");
        } catch (err) {
            console.error("Export Error:", err);
            showError("Export Failed", "Could not generate transaction log.");
        } finally {
            closeLoading();
        }
    };

    const handleViewInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setIsModalOpen(true);
    };

    const columns = [
        {
            header: 'Invoice details',
            key: 'invoiceNo',
            width: '25%',
            render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="tx-icon-box"><FaFileInvoiceDollar /></div>
                    <div>
                        <div style={{ fontWeight: 600, color: 'var(--primary-blue)' }}>{row.invoiceNo}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{row.courseId?.title}</div>
                    </div>
                </div>
            )
        },
        {
            header: 'Student',
            key: 'student',
            width: '20%',
            render: (row) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.studentId?.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{row.studentId?.email}</div>
                </div>
            )
        },
        {
            header: 'Amount',
            key: 'amount',
            width: '15%',
            render: (row) => (
                <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>
                    ${row.amount}
                </span>
            )
        },
        {
            header: 'Date',
            key: 'date',
            width: '15%',
            render: (row) => new Date(row.date).toLocaleDateString()
        },
        {
            header: 'Status',
            key: 'status',
            width: '10%',
            render: (row) => (
                <span className={`status-badge ${row.status.toLowerCase()}`}>
                    {row.status.toUpperCase()}
                </span>
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            width: '15%',
            render: (row) => (
                <div className="actions-cell">
                    <button
                        className="icon-btn view-btn"
                        title="View Invoice"
                        onClick={() => handleViewInvoice(row)}
                        style={{ color: '#6366F1' }}
                    >
                        <FaEye />
                    </button>
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    const totalRevenue = transactions.reduce((sum, t) => sum + t.amount, 0);

    return (
        <div className="payments fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Financial Operations</h1>
                <p className="page-subtitle">Platform-wide transaction integrity and revenue tracking.</p>
            </div>

            <div className="premium-card stats-strip">
                <div className="stat-pill">
                    <span className="pill-label">Total Revenue</span>
                    <span className="pill-value">${totalRevenue.toLocaleString()}</span>
                </div>
                <div className="stat-pill">
                    <span className="pill-label">Total Transactions</span>
                    <span className="pill-value blue">{transactions.length}</span>
                </div>
                <div className="stat-pill">
                    <span className="pill-label">Avg. Purchase</span>
                    <span className="pill-value yellow">${transactions.length > 0 ? (totalRevenue / transactions.length).toFixed(0) : 0}</span>
                </div>
            </div>

            <DataTable
                title="Revenue Stream"
                columns={columns}
                data={transactions}
                filterOptions={[
                    { label: 'Paid', value: 'Paid' },
                    { label: 'Refunded', value: 'Refunded' }
                ]}
                actions={
                    <button className="primary-btn" onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaDownload fontSize="14px" /> Export Master Ledger
                    </button>
                }
            />

            <InvoiceModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                invoice={selectedInvoice}
            />
        </div>
    );
};

export default Payments;
